import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 180_000 });
export const TOKEN_KEY = "buzzly_token";
http.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
http.interceptors.response.use((r) => r, (err) => {
  if (err?.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
    localStorage.removeItem(TOKEN_KEY);
    if (window.location.pathname.startsWith("/app")) window.location.href = "/login";
  }
  return Promise.reject(err);
});

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Une erreur est survenue";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
}

export const api = {
  register: (body) => http.post("/auth/register", body).then(r => r.data),
  login: (body) => http.post("/auth/login", body).then(r => r.data),
  me: () => http.get("/auth/me").then(r => r.data),
  updateProfile: (body) => http.patch("/profile", body).then(r => r.data),
  uploadAvatar: (file, onPct) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/profile/avatar", fd, { onUploadProgress: e => onPct?.(Math.round((e.loaded / (e.total || file.size)) * 100)) }).then(r => r.data);
  },
  deleteAvatar: () => http.delete("/profile/avatar").then(r => r.data),
  bloggers: () => http.get("/bloggers").then(r => r.data),
  bloggerVideo: (index) => http.post(`/bloggers/${index}/video`).then(r => r.data),
  spots: () => http.get("/spots").then(r => r.data),
  spotGet: (id) => http.get(`/spots/${id}`).then(r => r.data),
  spotAnalyze: (url) => http.post("/spots/analyze", { url }).then(r => r.data),
  spotUpdate: (id, body) => http.patch(`/spots/${id}`, body).then(r => r.data),
  spotAddPhoto: (id, file) => { const fd = new FormData(); fd.append("file", file); return http.post(`/spots/${id}/photos`, fd).then(r => r.data); },
  spotCheckout: (id, origin_url) => http.post(`/spots/${id}/checkout`, { origin_url }).then(r => r.data),
  spotConfirm: (id, session_id) => http.get(`/spots/${id}/confirm`, { params: { session_id } }).then(r => r.data),
  spotDelete: (id) => http.delete(`/spots/${id}`).then(r => r.data),
  spotRetry: (id) => http.post(`/spots/${id}/retry`).then(r => r.data),
  fbConnection: () => http.get("/fb/connection").then(r => r.data),
  fbConnect: (origin_url) => http.post("/fb/connect", { origin_url }).then(r => r.data),
  fbChoosePage: (page_id) => http.post("/fb/page", { page_id }).then(r => r.data),
  fbAutoPublish: (enabled) => http.patch("/fb/auto-publish", { enabled }).then(r => r.data),
  fbDisconnect: () => http.delete("/fb/connection").then(r => r.data),
  metaConnection: () => http.get("/meta/connection").then(r => r.data),
  metaConnect: (origin_url) => http.post("/meta/connect", { origin_url }).then(r => r.data),
  metaChoosePage: (page_id) => http.post("/meta/page", { page_id }).then(r => r.data),
  metaAddPage: (page) => http.post("/meta/page/add", { page }).then(r => r.data),
  metaSettings: (body) => http.patch("/meta/settings", body).then(r => r.data),
  metaDisconnect: () => http.delete("/meta/connection").then(r => r.data),
  referral: () => http.get("/referral").then(r => r.data),
  adminFbQuota: () => http.get("/admin/fb-quota").then(r => r.data),
  agent: () => http.get("/agent").then(r => r.data),
  saveAgent: (body) => http.put("/agent", body).then(r => r.data),
  checkout: () => http.post("/billing/checkout", { origin_url: window.location.origin }).then(r => r.data),
  adminOverview: () => http.get("/admin/overview").then(r => r.data),
  adminUsers: () => http.get("/admin/users").then(r => r.data),
  adminSuspend: (id, value) => http.post(`/admin/users/${id}/suspend`, { value }).then(r => r.data),
  adminComp: (id, value) => http.post(`/admin/users/${id}/comp`, { value }).then(r => r.data),
  paymentStatus: (sid) => http.get(`/payments/status/${sid}`).then(r => r.data),
  stats: () => http.get("/dashboard/stats").then(r => r.data),
  trendsToday: () => http.get("/trends/today").then(r => r.data),
  trends: (params = {}) => http.get("/trends", { params }).then(r => r.data),
  scanManual: () => http.post("/scan/manual", {}).then(r => r.data),
  scans: () => http.get("/scans").then(r => r.data),
  veilleStatus: () => http.get("/veille/status").then(r => r.data),
  veilleProfiles: () => http.get("/veille/profiles").then(r => r.data),
  generateDigest: () => http.post("/digest/generate").then(r => r.data),
  facebookStatus: () => http.get("/facebook/status").then(r => r.data),
  facebookCheck: (id) => http.post(`/videos/${id}/facebook-check`).then(r => r.data),
  facebookRetry: (id) => http.post(`/videos/${id}/facebook-retry`).then(r => r.data),
  videoFromLink: (url) => http.post("/videos/from-link", { url }, { timeout: 180000 }).then(r => r.data),
  videoCaption: (id) => http.get(`/videos/${id}/caption`).then(r => r.data.caption),
  videos: (params = {}) => http.get("/videos", { params }).then(r => r.data),
  video: (id) => http.get(`/videos/${id}`).then(r => r.data),
  generateVideo: (body) => http.post("/videos/generate", body).then(r => r.data),
  publishVideo: (videoId, scheduledFor = null) =>
    http.post("/videos/publish", { video_id: videoId, scheduled_for: scheduledFor }).then(r => r.data),
  deleteVideo: (id) => http.delete(`/videos/${id}`).then(r => r.data),
};

export const BRAND = {
  name: "Bertrand Chic Saumon",
  followers: 17000,
  avatar:
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_trendcast-saumon/artifacts/4ed852e055960df5_Image%2017-10-26%20a%CC%80%2020.24.webp",
  pageUrl: "https://www.facebook.com/profile.php?id=100083285157404",
};

export const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export const MODEL_LABELS = {
  montage: "Montage captures · Voix off",
  story: "Histoire 3-4 min · Récit complet",
  spot: "Spot pub · Publicité",
  digest: "Résumé du jour · Émission",
  veo3: "Veo 3 Fast · Google",
  kling: "Kling v2 Master",
  seedance: "Seedance 1 Pro · ByteDance",
};

export const STATUS_LABELS = {
  queued: "En file",
  rendering: "Rendu en cours",
  ready: "Prête",
  failed: "Échouée",
  published: "Publiée",
};
